<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { RouterLink, useRouter } from 'vue-router';

const { proxy } = getCurrentInstance();
const { $stores, $bus } = proxy;

const router = useRouter();

// Accéder aux stores via le proxy
const cartStore = $stores?.cart;
const productsStore = $stores?.products;
const userStore = $stores?.user;

const currentStep = ref(1);
const loading = ref(false);
const paymentError = ref(null);

const formData = ref({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  deliveryMethod: 'email',
  paymentMethod: 'mobilemoney',
});

// Articles du panier depuis le store
const orderItems = computed(() => cartStore?.state?.items || []);

// Totaux depuis le store
const subtotal = computed(() => cartStore?.state?.subtotal || 0);
const total = computed(() => cartStore?.state?.total || 0);

// Méthode de paiement unique
const paymentMethods = [
  { value: 'mobilemoney', name: 'Mobile Money', color: '#37b6e9', icon: 'smartphone' },
];

// Options de livraison
const deliveryOptions = [
  { value: 'email', name: 'Email instantané', description: 'Recevez vos codes immédiatement par email', icon: 'email' },
  { value: 'whatsapp', name: 'WhatsApp', description: 'Recevez vos codes par WhatsApp', icon: 'chat' },
];

// Validation du formulaire
const isFormValid = computed(() => {
  return formData.value.firstName.trim() &&
         formData.value.lastName.trim() &&
         formData.value.email.trim() &&
         formData.value.phone.trim();
});

// Afficher toast pour whatsapp indisponible
const handleWhatsAppClick = () => {
  window.$bus?.emit('toast.show', {
    type: 'info',
    title: 'Info',
    body: 'Livraison par WhatsApp bientôt disponible',
    timeout: 3000,
  });
};

// Soumettre la commande et lancer le paiement
const submitOrder = async () => {
  if (!isFormValid.value) {
    paymentError.value = 'Veuillez remplir tous les champs obligatoires';
    return;
  }

  if (orderItems.value.length === 0) {
    paymentError.value = 'Votre panier est vide';
    return;
  }

  loading.value = true;
  paymentError.value = null;

  try {
    // Préparer les données de paiement
    const paymentData = {
      phoneNumber: formData.value.phone,
      email: formData.value.email,
      firstName: formData.value.firstName,
      lastName: formData.value.lastName,
      userId: userStore?.state?.current?.id || 'guest',
      totalAmount: total.value,
      products: orderItems.value,
      operator: formData.value.paymentMethod,
    };

    // Émettre l'événement de lancement du paiement
    window.$bus?.emit('checkout:launch', {
      cartState: cartStore?.state,
      userData: paymentData,
    });

    // Écouter la réponse du paiement (mitt n'a pas 'once', on utilise 'on' avec cleanup)
    const paymentLaunchedHandler = (paymentResult) => {
      loading.value = false;

      if (paymentResult?.paymentUrl) {
        // Rediriger vers la page de paiement
        window.location.href = paymentResult.paymentUrl;
      } else if (paymentResult?.success) {
        // Mode simulation - afficher succès
        currentStep.value = 3; // Passer à l'étape confirmation
        // Afficher un message de succès (optionnel: rediriger vers une page de confirmation)
        alert(`Commande simulée avec succès!\nCommande #${paymentResult.orderId}\nMontant: ${paymentResult.amount.toLocaleString()} CFA`);
      } else {
        paymentError.value = 'Erreur lors du lancement du paiement';
      }
      window.$bus?.off('checkout:payment:launched', paymentLaunchedHandler);
    };

    const paymentErrorHandler = (error) => {
      paymentError.value = error.message || 'Erreur lors du paiement';
      loading.value = false;
      window.$bus?.off('checkout:payment:error', paymentErrorHandler);
    };

    window.$bus?.on('checkout:payment:launched', paymentLaunchedHandler);
    window.$bus?.on('checkout:payment:error', paymentErrorHandler);

  } catch (error) {
    console.error('Erreur soumission commande:', error);
    paymentError.value = error.message || 'Une erreur est survenue';
    loading.value = false;
  }
};

// Charger le panier au montage
onMounted(() => {
  cartStore?.loadCart();

  // Auto-fill with user data if logged in
  const currentUser = userStore?.state?.current;
  if (currentUser) {
    formData.value = {
      ...formData.value,
      firstName: currentUser.firstName || '',
      lastName: currentUser.lastName || '',
      email: currentUser.email || '',
      phone: currentUser.phoneNumber || '',
    };
  }
});
</script>

<template>
  <div class="min-h-screen" style="background: #0A0A0B;">
    <div class="max-w-7xl mx-auto px-6 lg:px-8 py-12">

      <!-- Page Header -->
      <div class="mb-12">
        <h1 class="font-black text-4xl md:text-5xl mb-4 text-white" style="font-family: 'Outfit', sans-serif;">
          <span style="background: linear-gradient(135deg, #37b6e9 0%, #4adef2 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">Finaliser la commande</span>
        </h1>
        <p class="text-white/60 text-lg">Vous êtes à quelques clics de vos abonnements</p>
      </div>

      <!-- Breadcrumb -->
      <nav aria-label="Breadcrumb" class="flex items-center text-sm text-white/50 mb-10">
        <RouterLink class="hover:text-white transition-colors" to="/">Accueil</RouterLink>
        <span class="mx-2">/</span>
        <RouterLink class="hover:text-white transition-colors" to="/cart">Panier</RouterLink>
        <span class="mx-2">/</span>
        <span class="text-white">Commander</span>
      </nav>

      <!-- Progress Steps -->
      <div class="mb-16">
        <div class="flex items-center justify-center max-w-2xl mx-auto">
          <div class="flex items-center">
            <div class="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl transition-all" :style="currentStep >= 1 ? 'background: linear-gradient(135deg, #37b6e9 0%, #2e9ac4 100%); box-shadow: 0 4px 15px rgba(124, 58, 237, 0.3);' : 'background: rgba(255, 255, 255, 0.05);'">1</div>
            <span class="ml-3 font-semibold hidden sm:inline" :class="currentStep >= 1 ? 'text-white' : 'text-white/40'">Coordonnées</span>
          </div>
          <div class="w-20 sm:w-32 h-0.5 mx-3" style="background: rgba(255, 255, 255, 0.1);"></div>
          <div class="flex items-center">
            <div class="w-14 h-14 rounded-full flex items-center justify-center font-bold text-xl transition-all" :style="currentStep >= 2 ? 'background: linear-gradient(135deg, #37b6e9 0%, #2e9ac4 100%); box-shadow: 0 4px 15px rgba(124, 58, 237, 0.3); color: white;' : 'background: rgba(255, 255, 255, 0.05); color: white/40;'">2</div>
            <span class="ml-3 font-semibold hidden sm:inline" :class="currentStep >= 2 ? 'text-white' : 'text-white/40'">Paiement</span>
          </div>
          <div class="w-20 sm:w-32 h-0.5 mx-3" style="background: rgba(255, 255, 255, 0.1);"></div>
          <div class="flex items-center">
            <div class="w-14 h-14 rounded-full flex items-center justify-center font-bold text-xl transition-all" :style="currentStep >= 3 ? 'background: linear-gradient(135deg, #10B981 0%, #059669 100%); box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3); color: white;' : 'background: rgba(255, 255, 255, 0.05); color: white/40;'">
              <span class="material-icons-outlined text-2xl" v-if="currentStep >= 3">check</span>
              <span v-else>3</span>
            </div>
            <span class="ml-3 font-semibold hidden sm:inline" :class="currentStep >= 3 ? 'text-white' : 'text-white/40'">Confirmation</span>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Checkout Form -->
        <div class="lg:col-span-2 space-y-8">

          <!-- Contact Information -->
          <section class="rounded-3xl p-8" style="background: rgba(26, 26, 29, 0.8); border: 1px solid rgba(255, 255, 255, 0.06);">
            <h2 class="text-xl font-bold mb-6 flex items-center gap-3 text-white" style="font-family: 'Outfit', sans-serif;">
              <span class="material-icons-outlined" style="color: #37b6e9;">person</span>
              Informations de contact
            </h2>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-semibold mb-3 text-white/70">Prénom *</label>
                <input v-model="formData.firstName" type="text" placeholder="Jean" class="w-full rounded-xl px-4 py-3 transition-all text-white" style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.06);"/>
              </div>
              <div>
                <label class="block text-sm font-semibold mb-3 text-white/70">Nom *</label>
                <input v-model="formData.lastName" type="text" placeholder="Dupont" class="w-full rounded-xl px-4 py-3 transition-all text-white" style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.06);"/>
              </div>
              <div>
                <label class="block text-sm font-semibold mb-3 text-white/70">Email *</label>
                <input v-model="formData.email" type="email" placeholder="jean.dupont@email.com" class="w-full rounded-xl px-4 py-3 transition-all text-white" style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.06);"/>
              </div>
              <div>
                <label class="block text-sm font-semibold mb-3 text-white/70">Téléphone *</label>
                <input v-model="formData.phone" type="tel" placeholder="+225 01 02 03 04 05" class="w-full rounded-xl px-4 py-3 transition-all text-white" style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.06);"/>
              </div>
            </div>
          </section>

          <!-- Delivery Method -->
          <section class="rounded-3xl p-8" style="background: rgba(26, 26, 29, 0.8); border: 1px solid rgba(255, 255, 255, 0.06);">
            <h2 class="text-xl font-bold mb-6 flex items-center gap-3 text-white" style="font-family: 'Outfit', sans-serif;">
              <span class="material-icons-outlined" style="color: #37b6e9;">local_shipping</span>
              Mode de livraison
            </h2>

            <div class="space-y-4">
              <!-- Email - Option sélectionnable -->
              <label class="flex items-center p-5 rounded-2xl cursor-pointer transition-all hover:scale-[1.01]" :class="formData.deliveryMethod === 'email' ? 'ring-2' : ''" :style="formData.deliveryMethod === 'email' ? 'background: rgba(124, 58, 237, 0.1); border-color: #37b6e9;' : 'background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06);'">
                <input v-model="formData.deliveryMethod" type="radio" value="email" class="w-5 h-5"/>
                <div class="ml-4 flex-1">
                  <span class="font-semibold text-white">Email instantané</span>
                  <p class="text-sm text-white/50">Recevez vos codes immédiatement par email</p>
                </div>
                <span class="font-bold px-3 py-1 rounded-full text-sm" style="background: rgba(16, 185, 129, 0.15); color: #10B981;">Gratuit</span>
              </label>

              <!-- WhatsApp - Désactivé avec toast -->
              <div class="flex items-center p-5 rounded-2xl cursor-pointer transition-all hover:scale-[1.01] opacity-60" style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06);" @click="handleWhatsAppClick">
                <div class="w-5 h-5 rounded-full border-2 border-white/30 flex items-center justify-center flex-shrink-0">
                  <div class="w-2.5 h-2.5 rounded-full bg-white/30"></div>
                </div>
                <div class="ml-4 flex-1">
                  <span class="font-semibold text-white">WhatsApp</span>
                  <p class="text-sm text-white/50">Recevez vos codes par WhatsApp</p>
                </div>
                <span class="font-bold px-3 py-1 rounded-full text-sm" style="background: rgba(251, 191, 36, 0.15); color: #FBBF24;">Bientôt</span>
              </div>
            </div>
          </section>

          <!-- Payment Method -->
          <section class="rounded-3xl p-8" style="background: rgba(26, 26, 29, 0.8); border: 1px solid rgba(255, 255, 255, 0.06);">
            <h2 class="text-xl font-bold mb-6 flex items-center gap-3 text-white" style="font-family: 'Outfit', sans-serif;">
              <span class="material-icons-outlined" style="color: #37b6e9;">account_balance_wallet</span>
              Mode de paiement
            </h2>

            <!-- Mobile Money - Single option, already selected -->
            <div class="p-6 rounded-2xl transition-all" style="background: rgba(55, 182, 233, 0.1); border: 2px solid #37b6e9;">
              <div class="flex items-center gap-4">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style="background: linear-gradient(135deg, #37b6e9 0%, #2e9ac4 100%); box-shadow: 0 4px 15px rgba(55, 182, 233, 0.3);">
                  <span class="material-icons-outlined text-white text-2xl">smartphone</span>
                </div>
                <div class="flex-1 min-w-0">
                  <span class="font-bold text-xl text-white block">Mobile Money</span>
                  <p class="text-sm text-white/60 mt-1">Paiement rapide et sécurisé via votre mobile</p>
                </div>
                <div class="flex items-center gap-2 text-white/60 flex-shrink-0">
                  <span class="material-icons-outlined" style="color: #10B981;">check_circle</span>
                  <span class="text-sm font-medium hidden sm:inline">Sélectionné</span>
                </div>
              </div>
            </div>

            <!-- Security Info -->
            <div class="mt-6 p-4 rounded-xl" style="background: rgba(59, 130, 246, 0.1); border: 1px solid rgba(59, 130, 246, 0.2);">
              <p class="text-sm text-white/70 flex items-start gap-2">
                <span class="material-icons-outlined text-lg" style="color: #3B82F6;">info</span>
                <span>Paiement sécurisé. Vos informations sont protégées et ne sont jamais stockées.</span>
              </p>
            </div>
          </section>

        </div>

        <!-- Order Summary -->
        <div class="lg:col-span-1">
          <div class="rounded-3xl p-8 sticky top-28" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <h2 class="text-xl font-bold mb-6 text-white" style="font-family: 'Outfit', sans-serif;">Récapitulatif</h2>

            <!-- Order Items -->
            <div class="space-y-4 mb-6 pb-6" style="border-bottom: 1px solid rgba(255, 255, 255, 0.06);">
              <div v-for="item in orderItems" :key="item.id" class="flex gap-4">
                <div class="w-16 h-16 rounded-lg flex items-center justify-center flex-shrink-0" style="background: rgba(124, 58, 237, 0.08);">
                  <img v-if="item.image" :alt="item.name" class="w-full h-full object-contain p-2" :src="item.image"/>
                  <span v-else class="material-icons-outlined text-2xl text-white/30">subscriptions</span>
                </div>
                <div class="flex-1">
                  <h4 class="font-semibold text-sm text-white">{{ item.name || item.title }}</h4>
                  <p class="text-xs text-white/50">{{ item.options?.duration || item.options?.variant || `Quantité: ${item.quantity}` }}</p>
                  <p class="font-bold text-sm mt-1" style="color: #37b6e9;">{{ (item.price * item.quantity).toLocaleString() }} CFA</p>
                </div>
              </div>
            </div>

            <!-- Totals -->
            <div class="space-y-3 mb-8">
              <div class="flex justify-between text-sm">
                <span class="text-white/50">Sous-total</span>
                <span class="font-semibold text-white">{{ subtotal.toLocaleString() }} CFA</span>
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-white/50">Livraison</span>
                <span class="font-semibold" style="color: #10B981;">Gratuite</span>
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-white/50">Réduction</span>
                <span class="font-semibold text-white/40">- 0 CFA</span>
              </div>
              <div class="pt-4 flex justify-between" style="border-top: 1px solid rgba(255, 255, 255, 0.06);">
                <span class="font-bold text-lg text-white">Total</span>
                <span class="font-black text-2xl text-white" style="font-family: 'Outfit', sans-serif;">{{ total.toLocaleString() }} CFA</span>
              </div>
            </div>

            <!-- Error Message -->
            <div v-if="paymentError" class="mb-6 p-4 rounded-xl" style="background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.2);">
              <p class="text-sm text-white/80 flex items-start gap-2">
                <span class="material-icons-outlined text-lg" style="color: #EF4444;">error</span>
                <span>{{ paymentError }}</span>
              </p>
            </div>

            <!-- Submit Button -->
            <button
              @click="submitOrder"
              :disabled="loading || !isFormValid || orderItems.length === 0"
              class="w-full flex items-center justify-center gap-3 py-4 rounded-2xl font-bold transition-all hover:scale-[1.02] hover:shadow-xl active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              style="background: linear-gradient(135deg, #37b6e9 0%, #2e9ac4 100%); color: white; box-shadow: 0 4px 20px rgba(124, 58, 237, 0.3);"
            >
              <span v-if="loading" class="material-icons-outlined animate-spin">refresh</span>
              <span v-else class="material-icons-outlined">lock</span>
              <span>{{ loading ? 'Traitement...' : `Payer ${total.toLocaleString()} CFA` }}</span>
            </button>

            <p class="text-xs text-white/40 text-center mt-4 flex items-center justify-center gap-1">
              <span class="material-icons-outlined text-sm">verified_user</span>
              <span>En cliquant, vous acceptez nos conditions générales</span>
            </p>

            <!-- Security Badges -->
            <div class="mt-8 pt-6 grid grid-cols-3 gap-4" style="border-top: 1px solid rgba(255, 255, 255, 0.06);">
              <div class="text-center">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2" style="background: rgba(16, 185, 129, 0.1);">
                  <span class="material-icons-outlined" style="color: #10B981;">security</span>
                </div>
                <p class="text-xs text-white/50">Sécurisé</p>
              </div>
              <div class="text-center">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2" style="background: rgba(124, 58, 237, 0.1);">
                  <span class="material-icons-outlined" style="color: #37b6e9;">flash_on</span>
                </div>
                <p class="text-xs text-white/50">Instantané</p>
              </div>
              <div class="text-center">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2" style="background: rgba(212, 175, 55, 0.1);">
                  <span class="material-icons-outlined" style="color: #4b4ced;">support_agent</span>
                </div>
                <p class="text-xs text-white/50">Support 24/7</p>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>
